import React, { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import { PaymentElement, XPay, useXpay } from '@xstak/xpay-element';
import './BookingPage.css';

const STEPS = {
  CHECK: 'check',
  PAY: 'pay',
  DONE: 'done',
};

const BOOKING_PRICE_PKR = 15000;

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Karachi', label: 'Asia/Karachi (Pakistan)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai' },
  { value: 'Europe/London', label: 'Europe/London' },
  { value: 'America/New_York', label: 'America/New_York' },
];

function formatPkr(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return String(amount);
  return new Intl.NumberFormat(undefined).format(value);
}

function pickXpayIntent(data) {
  if (!data) return null;
  // Backend returns { ok, data: { data: <xpay_response>, message, responseStatus, success } }
  // Xpay responses vary; try common shapes.
  const candidate = data?.data?.data?.resource || data?.data?.data || data?.data?.resource || data?.data || data?.resource || data;
  if (!candidate || typeof candidate !== 'object') return null;
  return candidate;
}

function getXpayClientSecret(intent) {
  return intent?.client_secret || intent?.clientSecret || intent?.pi_client_secret || '';
}

function getXpayEncryptionKey(intent) {
  return intent?.encryptionKey || intent?.encryption_key || '';
}

function getXpayHmacSecret(intent) {
  return intent?.hmacSecret || intent?.hmac_secret || '';
}

function XpayPaymentForm({ intent, customer, amountLabel, onPaid }) {
  const xpay = useXpay();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  const clientSecret = getXpayClientSecret(intent);
  const encryptionKey = getXpayEncryptionKey(intent);

  const options = useMemo(
    () => ({
      override: true,
      style: {
        '.input': {
          border: '1px solid rgba(0, 0, 0, 0.18)',
          'border-radius': '14px',
          padding: '10px 12px',
        },
        '.label': {
          'font-size': '14px',
        },
      },
    }),
    []
  );

  const pay = async () => {
    setError('');
    if (!clientSecret || !encryptionKey) {
      setError('Payment is not ready yet. Please try again in a moment.');
      return;
    }

    setPaying(true);
    try {
      const result = await xpay.confirmPayment('card', clientSecret, customer, encryptionKey);
      if (result?.error) {
        setError(result?.error?.message || String(result.error));
        return;
      }
      onPaid(result);
    } catch (e) {
      setError(e?.message || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="XpayBody">
      <PaymentElement options={options} />

      {error && (
        <div className="Alert" role="alert">
          <div className="AlertTitle">Payment failed</div>
          <div className="AlertBody">{error}</div>
        </div>
      )}

      <button className="PrimaryButton" type="button" onClick={pay} disabled={paying}>
        {paying ? 'Processing…' : `Pay PKR ${amountLabel}`}
      </button>
    </div>
  );
}

function XpayModal({ open, onClose, intent, customer, onPaid }) {
  const publishableKey = (process.env.REACT_APP_XPAY_PUBLISHABLE_KEY || '').trim();
  const accountId = (process.env.REACT_APP_XPAY_ACCOUNT_ID || '').trim();
  const hmacSecret = getXpayHmacSecret(intent);
  const clientSecret = getXpayClientSecret(intent);
  const encryptionKey = getXpayEncryptionKey(intent);

  if (!open) return null;

  const missing = [];
  if (!publishableKey) missing.push('REACT_APP_XPAY_PUBLISHABLE_KEY');
  if (!accountId) missing.push('REACT_APP_XPAY_ACCOUNT_ID');
  if (!hmacSecret) missing.push('Xpay hmac secret (from backend)');
  if (!clientSecret) missing.push('Xpay client secret (from backend)');
  if (!encryptionKey) missing.push('Xpay encryption key (from backend)');

  return (
    <div className="XpayOverlay" role="dialog" aria-modal="true" aria-label="Xpay payment">
      <div className="XpayModal">
        <div className="XpayHeader">
          <div className="XpayTitle">Payment</div>
          <button className="XpayClose" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {missing.length > 0 ? (
          <div className="Alert" role="alert">
            <div className="AlertTitle">Xpay is not configured</div>
            <div className="AlertBody">Missing: {missing.join(', ')}</div>
          </div>
        ) : (
          <XPay xpay={{ publishableKey, hmacSecret, accountId }}>
            <XpayPaymentForm
              intent={intent}
              customer={customer}
              amountLabel={formatPkr(BOOKING_PRICE_PKR)}
              onPaid={onPaid}
            />
          </XPay>
        )}
      </div>
    </div>
  );
}

function toLocalIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function fromLocalIsoDate(isoDate) {
  if (!isoDate || typeof isoDate !== 'string') return undefined;
  const parts = isoDate.split('-');
  if (parts.length !== 3) return undefined;
  const year = Number(parts[0]);
  const monthIndex = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(monthIndex) ||
    !Number.isFinite(day) ||
    monthIndex < 0 ||
    monthIndex > 11
  ) {
    return undefined;
  }
  const d = new Date(year, monthIndex, day);
  if (Number.isNaN(d.getTime())) return undefined;
  return d;
}

function toIsoOrEmpty(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  return '';
}

function normalizeIsoLike(value) {
  if (!value || typeof value !== 'string') return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.trim();
  // Normalize to UTC ISO with seconds precision for stable matching.
  const iso = d.toISOString();
  return iso.replace(/\.\d{3}Z$/, 'Z');
}

function extractAvailableTimes(availabilityPayload) {
  if (!availabilityPayload) return [];

  const availableRaw =
    (Array.isArray(availabilityPayload.collection) && availabilityPayload.collection) ||
    (Array.isArray(availabilityPayload.available_times) && availabilityPayload.available_times) ||
    (Array.isArray(availabilityPayload.times) && availabilityPayload.times) ||
    [];

  const bookedRaw =
    (Array.isArray(availabilityPayload.booked_times) && availabilityPayload.booked_times) ||
    (Array.isArray(availabilityPayload.bookedTimes) && availabilityPayload.bookedTimes) ||
    [];

  const bookedSet = new Set(
    bookedRaw
      .filter((t) => typeof t === 'string' && t.trim())
      .map((t) => normalizeIsoLike(t))
      .filter(Boolean)
  );

  const merged = new Map();

  for (const item of availableRaw) {
    const start = extractStartTime(item);
    const key = normalizeIsoLike(start);
    if (!key) continue;
    merged.set(key, { start_time: key, booked: false });
  }

  for (const key of bookedSet) {
    const existing = merged.get(key);
    if (existing) {
      merged.set(key, { ...existing, booked: true });
    } else {
      merged.set(key, { start_time: key, booked: true });
    }
  }

  return Array.from(merged.values()).sort((a, b) => {
    const ta = new Date(a.start_time).getTime();
    const tb = new Date(b.start_time).getTime();
    if (!Number.isFinite(ta) || !Number.isFinite(tb)) return String(a.start_time).localeCompare(String(b.start_time));
    return ta - tb;
  });
}

function formatAvailableTimeLabel(timeItem) {
  const candidate =
    (timeItem && typeof timeItem === 'object' && (timeItem.start_time || timeItem.startTime)) ||
    (typeof timeItem === 'string' ? timeItem : null);

  if (!candidate) return String(timeItem);

  const date = new Date(candidate);
  if (Number.isNaN(date.getTime())) return String(candidate);

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function extractStartTime(timeItem) {
  if (!timeItem) return '';
  if (typeof timeItem === 'string') return timeItem;
  if (typeof timeItem === 'object') {
    return timeItem.start_time || timeItem.startTime || '';
  }
  return '';
}

function formatLocalDateTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return String(isoString);
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function looksLikeEmail(email) {
  const value = (email || '').trim();
  if (!value) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function normalizePhone(value) {
  return String(value || '').trim();
}

function looksLikePhone(value) {
  const phone = normalizePhone(value);
  if (!phone) return false;
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 7;
}

function isSameLocalDay(a, b) {
  if (!(a instanceof Date) || !(b instanceof Date)) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default function BookingPage() {
  const [step, setStep] = useState(STEPS.CHECK);
  const [selectedDate, setSelectedDate] = useState(() => toLocalIsoDate(new Date()));
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');

  const [selectedStartTime, setSelectedStartTime] = useState('');

  const [inviteeName, setInviteeName] = useState('');
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [inviteePhone, setInviteePhone] = useState('');
  const [inviteeTimezone, setInviteeTimezone] = useState(() => 'Asia/Karachi');

  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingResult, setBookingResult] = useState(null);

  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  const [xpayIntent, setXpayIntent] = useState(null);
  const [xpayIntentLoading, setXpayIntentLoading] = useState(false);
  const [xpayIntentError, setXpayIntentError] = useState('');
  const [xpayModalOpen, setXpayModalOpen] = useState(false);
  const [xpayOpenOnReady, setXpayOpenOnReady] = useState(false);

  const availableTimes = useMemo(
    () => extractAvailableTimes(availabilityResult),
    [availabilityResult]
  );

  useEffect(() => {
    if (!selectedStartTime) return;
    const selectedAvailability = availableTimes.find(
      (t) => normalizeIsoLike(extractStartTime(t)) === normalizeIsoLike(selectedStartTime)
    );

    if (selectedAvailability && typeof selectedAvailability === 'object' && selectedAvailability.booked) {
      setSelectedStartTime('');
      setBookingError('');
    }
  }, [availableTimes, selectedStartTime]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const selectedDateValue = useMemo(() => {
    return fromLocalIsoDate(selectedDate);
  }, [selectedDate]);

  const finalizeBooking = () => {
    setBookingError('');
    setBookingResult(null);

    const startTime = toIsoOrEmpty(selectedStartTime);
    if (!startTime) {
      setBookingError('Please select a time.');
      return;
    }

    const selectedAvailability = availableTimes.find(
      (t) => normalizeIsoLike(extractStartTime(t)) === normalizeIsoLike(selectedStartTime)
    );
    if (selectedAvailability && typeof selectedAvailability === 'object' && selectedAvailability.booked) {
      setBookingError('this time has already been booked');
      return;
    }

    const name = inviteeName.trim();
    const email = inviteeEmail.trim();
    const phone = normalizePhone(inviteePhone);
    const timezone = inviteeTimezone.trim() || 'UTC';

    if (!name) {
      setBookingError('Please enter your full name.');
      return;
    }

    if (!looksLikeEmail(email)) {
      setBookingError('Please enter a valid email address.');
      return;
    }

    if (!looksLikePhone(phone)) {
      setBookingError('Please enter a valid contact number.');
      return;
    }

    setBookingLoading(true);
    fetch('/api/create-invitee', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        start_time: startTime,
        invitee: {
          name,
          email,
          phone,
          timezone,
        },
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || 'Failed to create booking');
        }
        setBookingResult(data?.data || null);
        setStep(STEPS.DONE);
      })
      .catch((err) => {
        setBookingError(err?.message || 'Failed to create booking');
      })
      .finally(() => {
        setBookingLoading(false);
      });
  };

  const prepareXpayIntent = () => {
    if (xpayIntent || xpayIntentLoading) return;

    const name = inviteeName.trim();
    const email = inviteeEmail.trim();
    const phone = normalizePhone(inviteePhone);
    if (!name || !looksLikeEmail(email) || !looksLikePhone(phone)) return;

    setXpayIntentError('');
    setXpayIntentLoading(true);
    fetch('/api/xpay/payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer: {
          name,
          email,
          phone,
        },
        metadata: {
          order_reference: `amelle_booking_${selectedStartTime || ''}`,
        },
      }),
    })
      .then(async (res) => {
        const rawText = await res.text();
        let data = {};
        try {
          data = rawText ? JSON.parse(rawText) : {};
        } catch (e) {
          data = {};
        }

        if (!res.ok || !data?.ok) {
          const serverError = data?.error || data?.message;
          const fallback = rawText && rawText.length < 600 ? rawText : '';
          const details = serverError || fallback || 'Failed to prepare payment';
          throw new Error(`(${res.status}) ${details}`);
        }
        const intent = pickXpayIntent(data);
        if (!intent) throw new Error('Xpay returned an unexpected response');
        setXpayIntent({
          ...intent,
          hmacSecret: data?.hmac_secret || data?.hmacSecret || '',
        });
      })
      .catch((e) => {
        setXpayIntentError(e?.message || 'Failed to prepare payment');
        setXpayOpenOnReady(false);
      })
      .finally(() => {
        setXpayIntentLoading(false);
      });
  };

  useEffect(() => {
    if (step !== STEPS.PAY) return;
    if (paymentConfirmed) return;
    prepareXpayIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, paymentConfirmed]);

  useEffect(() => {
    if (!xpayOpenOnReady) return;
    if (!xpayIntent) return;
    setXpayOpenOnReady(false);
    setXpayModalOpen(true);
  }, [xpayOpenOnReady, xpayIntent]);

  const checkAvailability = () => {
    setAvailabilityError('');
    setAvailabilityResult(null);

    if (!selectedDate) {
      setAvailabilityError('Please select a date.');
      return;
    }

    // Treat the selected date as a local day window.
    const dayStart = fromLocalIsoDate(selectedDate);
    if (!dayStart) {
      setAvailabilityError('Please select a valid date.');
      return;
    }
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);

    const now = new Date();
    const minFuture = new Date(now.getTime() + 5 * 60 * 1000);

    if (dayEnd.getTime() <= now.getTime()) {
      setAvailabilityError('Please select a future date.');
      return;
    }

    const startTime = new Date(Math.max(dayStart.getTime(), minFuture.getTime()));

    const startIso = startTime.toISOString();
    const endIso = dayEnd.toISOString();

    setAvailabilityLoading(true);
    fetch(
      `/api/available-times?start_time=${encodeURIComponent(startIso)}&end_time=${encodeURIComponent(
        endIso
      )}`
    )
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || 'Failed to check availability');
        }
        setAvailabilityResult(data?.data || null);
      })
      .catch((err) => {
        setAvailabilityError(err?.message || 'Failed to check availability');
      })
      .finally(() => {
        setAvailabilityLoading(false);
      });
  };

  const proceedToPayment = () => {
    setBookingError('');

    setXpayIntent(null);
    setXpayIntentError('');
    setXpayModalOpen(false);
    setXpayOpenOnReady(false);
    setPaymentConfirmed(false);

    if (!selectedStartTime) {
      setBookingError('Please select a time first.');
      return;
    }

    const selectedAvailability = availableTimes.find(
      (t) => normalizeIsoLike(extractStartTime(t)) === normalizeIsoLike(selectedStartTime)
    );
    if (selectedAvailability && typeof selectedAvailability === 'object' && selectedAvailability.booked) {
      setBookingError('this time has already been booked');
      return;
    }
    if (!inviteeName.trim()) {
      setBookingError('Please enter your full name.');
      return;
    }
    if (!looksLikeEmail(inviteeEmail)) {
      setBookingError('Please enter a valid email address.');
      return;
    }

    if (!looksLikePhone(inviteePhone)) {
      setBookingError('Please enter a valid contact number.');
      return;
    }

    setStep(STEPS.PAY);
  };

  const onCalendarChange = (value) => {
    const date = Array.isArray(value) ? value[0] : value;
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;

    setSelectedDate(toLocalIsoDate(date));
    setAvailabilityResult(null);
    setAvailabilityError('');
    setSelectedStartTime('');
  };

  const onSelectTime = (timeItem) => {
    const startTime = extractStartTime(timeItem);
    if (!startTime) return;
    setSelectedStartTime(normalizeIsoLike(startTime));
    setBookingError('');
  };

  const selectedTimeLabel = useMemo(
    () => (selectedStartTime ? formatLocalDateTime(selectedStartTime) : ''),
    [selectedStartTime]
  );

  return (
    <div className="BookingPage">
      <XpayModal
        open={xpayModalOpen}
        onClose={() => setXpayModalOpen(false)}
        intent={xpayIntent}
        customer={{
          name: inviteeName.trim(),
          email: inviteeEmail.trim(),
          phone: normalizePhone(inviteePhone),
        }}
        onPaid={() => {
          setXpayModalOpen(false);
          setPaymentConfirmed(true);
          setXpayIntent(null);
          setXpayIntentError('');
          setXpayOpenOnReady(false);
          finalizeBooking();
        }}
      />
      <div className="BookingLayout">
        <aside className="BookingVisual" aria-hidden="true">
          <div
            className="BookingVisual-photo"
            style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/arsalan.jpg)` }}
          />
          <div className="BookingVisual-water" />
        </aside>

        <section className="BookingPanel" aria-label="Amelle booking">
          <header className="BookingTop">
            <img
              className="BookingLogo"
              src={`${process.env.PUBLIC_URL}/amellecropped.png`}
              alt="Amelle"
            />
            <div className="BookingTopText">
              <div className="BookingKicker">Booking</div>
               <h1 className="BookingHeading">AN APPOINTMENT WITH ARSALAN</h1>
              
            </div>
          </header>

          <nav className="BookingSteps" aria-label="Progress">
            <div className={step === STEPS.CHECK ? 'Step Step--active' : 'Step'}>
              1. Details
            </div>
            <div className={step === STEPS.PAY ? 'Step Step--active' : 'Step'}>
              2. Payment
            </div>
            <div className={step === STEPS.DONE ? 'Step Step--active' : 'Step'}>
              3. Confirmation
            </div>
          </nav>

          <div className="BookingStage" data-step={step}>
            <section className="StagePane StagePane--check" aria-label="Check Date">
              <h2 className="PaneTitle">Check Date</h2>
              <p className="PaneText">Choose a date, then select a time and enter your details.</p>

              <div className="CalendarWrap" aria-label="Calendar">
                <Calendar
                  className="AmelleCalendar"
                  onChange={onCalendarChange}
                  value={selectedDateValue || today}
                  tileClassName={({ date, view }) =>
                    view === 'month' && selectedDateValue && isSameLocalDay(date, selectedDateValue)
                      ? 'AmelleTileSelected'
                      : null
                  }
                  minDate={today}
                  showNeighboringMonth={false}
                  prev2Label={null}
                  next2Label={null}
                />
              </div>

              <div className="SelectedDate">
                <span className="SelectedDateLabel">Selected</span>
                <span className="SelectedDateValue">
                  {selectedDate || '—'}
                </span>
              </div>

              {availabilityError && (
                <div className="Alert" role="alert">
                  <div className="AlertTitle">Availability check failed</div>
                  <div className="AlertBody">{availabilityError}</div>
                </div>
              )}

              <button
                className="PrimaryButton"
                type="button"
                onClick={checkAvailability}
                disabled={availabilityLoading}
              >
                {availabilityLoading ? 'Checking…' : 'Check Availability'}
              </button>

              {availabilityResult && (
                <div className="AvailabilityInline" aria-live="polite">
                  {availableTimes.length > 0 ? (
                    <>
                      {(() => {
                        const availableCount = availableTimes.filter(
                          (t) => !(t && typeof t === 'object' && t.booked)
                        ).length;
                        const bookedCount = availableTimes.length - availableCount;
                        const headline =
                          bookedCount > 0
                            ? `Available: ${availableCount} • Booked: ${bookedCount}`
                            : `Available: ${availableCount}`;

                        return <div className="AvailabilityHeadline">{headline}</div>;
                      })()}
                      <div className="AvailabilityTimes" aria-label="Available times (local time)">
                        {availableTimes.map((t) => {
                          const key =
                            (t && typeof t === 'object' && (t.start_time || t.startTime)) ||
                            (typeof t === 'string' ? t : JSON.stringify(t));
                          const startTime = normalizeIsoLike(extractStartTime(t));
                          const isBooked = Boolean(t && typeof t === 'object' && t.booked);
                          const isSelected =
                            !isBooked && Boolean(startTime) && startTime === selectedStartTime;
                          return (
                            <button
                              key={key}
                              className={
                                isBooked
                                  ? 'TimePill TimePill--disabled'
                                  : isSelected
                                    ? 'TimePill TimePill--selected'
                                    : 'TimePill'
                              }
                              type="button"
                              disabled={isBooked}
                              onClick={() => onSelectTime(t)}
                              aria-pressed={isSelected}
                              aria-disabled={isBooked}
                            >
                              {formatAvailableTimeLabel(t)}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    'No availability found for this date.'
                  )}
                </div>
              )}

              {availabilityResult && availableTimes.some((t) => !(t && typeof t === 'object' && t.booked)) && (
                <div className="DetailsCard" aria-label="Invitee details">
                  <div className="DetailsTitle">Your details</div>
                  <div className="DetailsGrid">
                    <label className="Field">
                      <span className="FieldLabel">Selected time</span>
                      <input
                        className="TextInput"
                        type="text"
                        value={selectedTimeLabel || '—'}
                        readOnly
                      />
                    </label>

                    <label className="Field">
                      <span className="FieldLabel">Full name</span>
                      <input
                        className="TextInput"
                        type="text"
                        value={inviteeName}
                        onChange={(e) => setInviteeName(e.target.value)}
                        placeholder="Jane Doe"
                        autoComplete="name"
                      />
                    </label>

                    <label className="Field">
                      <span className="FieldLabel">Email</span>
                      <input
                        className="TextInput"
                        type="email"
                        value={inviteeEmail}
                        onChange={(e) => setInviteeEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoComplete="email"
                      />
                    </label>

                    <label className="Field">
                      <span className="FieldLabel">Contact number</span>
                      <input
                        className="TextInput"
                        type="tel"
                        value={inviteePhone}
                        onChange={(e) => setInviteePhone(e.target.value)}
                        placeholder="0300 1234567"
                        autoComplete="tel"
                      />
                    </label>

                    <label className="Field">
                      <span className="FieldLabel">Timezone</span>
                      <select
                        className="TextInput"
                        value={inviteeTimezone}
                        onChange={(e) => setInviteeTimezone(e.target.value)}
                      >
                        {!TIMEZONE_OPTIONS.some((opt) => opt.value === inviteeTimezone) && (
                          <option value={inviteeTimezone}>{inviteeTimezone}</option>
                        )}
                        {TIMEZONE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              )}

              {bookingError && step === STEPS.CHECK && (
                <div className="Alert" role="alert">
                  <div className="AlertTitle">Please fix this</div>
                  <div className="AlertBody">{bookingError}</div>
                </div>
              )}

              <button
                className="SecondaryButton"
                type="button"
                onClick={proceedToPayment}
                disabled={
                  !availabilityResult ||
                  availableTimes.length === 0 ||
                  !selectedStartTime ||
                  !inviteeName.trim() ||
                  !looksLikeEmail(inviteeEmail) ||
                  !looksLikePhone(inviteePhone) ||
                  !inviteeTimezone.trim()
                }
              >
                Proceed to Payment
              </button>
            </section>

            <section className="StagePane StagePane--pay" aria-label="Payment">
              <h2 className="PaneTitle">Payment</h2>
              <p className="PaneText">Payment must be confirmed before we finalize your booking.</p>

              <div className="Summary">
                <div className="SummaryTitle">Payment details</div>
                <div className="SummaryRow">
                  <span className="SummaryLabel">Time</span>
                  <span className="SummaryValue">{selectedTimeLabel || '—'}</span>
                </div>
                <div className="SummaryRow">
                  <span className="SummaryLabel">Timezone</span>
                  <span className="SummaryValue">{inviteeTimezone || '—'}</span>
                </div>
                <div className="SummaryRow">
                  <span className="SummaryLabel">Name</span>
                  <span className="SummaryValue">{inviteeName || '—'}</span>
                </div>
                <div className="SummaryRow">
                  <span className="SummaryLabel">Email</span>
                  <span className="SummaryValue">{inviteeEmail || '—'}</span>
                </div>

                <div className="SummaryRow">
                  <span className="SummaryLabel">Contact</span>
                  <span className="SummaryValue">{inviteePhone || '—'}</span>
                </div>
                <div className="SummaryRow">
                  <span className="SummaryLabel">Amount</span>
                  <span className="SummaryValue SummaryValue--strong">
                    {`PKR ${formatPkr(BOOKING_PRICE_PKR)}`}
                  </span>
                </div>
              </div>

              {bookingError && step === STEPS.PAY && (
                <div className="Alert" role="alert">
                  <div className="AlertTitle">Unable to continue</div>
                  <div className="AlertBody">{bookingError}</div>
                </div>
              )}

              {xpayIntentError && (
                <div className="Alert" role="alert">
                  <div className="AlertTitle">Unable to start payment</div>
                  <div className="AlertBody">{xpayIntentError}</div>
                </div>
              )}

              {paymentConfirmed && (
                <div className="Summary" aria-label="Payment received">
                  <div className="SummaryTitle">Payment received</div>
                  <div className="SummaryRow">
                    <span className="SummaryLabel">Status</span>
                    <span className="SummaryValue">Paid</span>
                  </div>
                  <div className="SummaryRow">
                    <span className="SummaryLabel">Amount</span>
                    <span className="SummaryValue SummaryValue--strong">
                      {`PKR ${formatPkr(BOOKING_PRICE_PKR)}`}
                    </span>
                  </div>
                </div>
              )}

              <button
                className="PrimaryButton"
                type="button"
                onClick={() => {
                  // If payment already succeeded, do NOT attempt to pay again.
                  // Use the same button to retry finalizing the booking.
                  if (paymentConfirmed) {
                    finalizeBooking();
                    return;
                  }

                  setXpayIntentError('');
                  if (xpayIntent) {
                    setXpayModalOpen(true);
                    return;
                  }
                  setXpayOpenOnReady(true);
                  prepareXpayIntent();
                }}
                disabled={bookingLoading || xpayModalOpen}
              >
                {bookingLoading
                  ? 'Finalizing…'
                  : xpayIntentLoading && !paymentConfirmed
                    ? 'Preparing payment…'
                    : `Pay PKR ${formatPkr(BOOKING_PRICE_PKR)}`}
              </button>

              <button
                className="SecondaryButton"
                type="button"
                onClick={() => {
                  setXpayModalOpen(false);
                  setXpayIntent(null);
                  setXpayIntentError('');
                  setXpayOpenOnReady(false);
                  setPaymentConfirmed(false);
                  setStep(STEPS.CHECK);
                }}
                disabled={bookingLoading}
              >
                Back
              </button>
            </section>

            <section className="StagePane StagePane--done" aria-label="Done">
              <h2 className="PaneTitle">Booked</h2>
              <p className="PaneText">Your appointment is scheduled.</p>

              <div className="Summary" aria-label="Booked meeting details">
                <div className="SummaryTitle">Meeting details</div>
                <div className="SummaryRow">
                  <span className="SummaryLabel">Time</span>
                  <span className="SummaryValue">{selectedTimeLabel || '—'}</span>
                </div>
                <div className="SummaryRow">
                  <span className="SummaryLabel">Timezone</span>
                  <span className="SummaryValue">{inviteeTimezone || '—'}</span>
                </div>
                <div className="SummaryRow">
                  <span className="SummaryLabel">Name</span>
                  <span className="SummaryValue">{inviteeName || '—'}</span>
                </div>
                <div className="SummaryRow">
                  <span className="SummaryLabel">Email</span>
                  <span className="SummaryValue">{inviteeEmail || '—'}</span>
                </div>

                <div className="SummaryRow">
                  <span className="SummaryLabel">Contact</span>
                  <span className="SummaryValue">{inviteePhone || '—'}</span>
                </div>
              </div>

              {bookingResult?.resource?.uri && (
                <div className="Result" aria-label="Booking result">
                  <div className="ResultTitle">Confirmation</div>
                  <div className="ResultBody">
                    Your Calendly invitee has been created.
                  </div>
                  {(bookingResult?.resource?.reschedule_url || bookingResult?.resource?.cancel_url) && (
                    <div className="ResultExamples">
                      {bookingResult?.resource?.reschedule_url && (
                        <a href={bookingResult.resource.reschedule_url} target="_blank" rel="noreferrer">
                          Reschedule
                        </a>
                      )}
                      {bookingResult?.resource?.reschedule_url && bookingResult?.resource?.cancel_url
                        ? ' · '
                        : null}
                      {bookingResult?.resource?.cancel_url && (
                        <a href={bookingResult.resource.cancel_url} target="_blank" rel="noreferrer">
                          Cancel
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>

          <div className="HelplineNote HelplineNote--bottom" role="note" aria-label="Helpline">
            <span className="HelplineNoteLabel">Helpline:</span>{' '}
            <span className="HelplineNoteNumber">0300 1AMELLE (263553)</span>
          </div>

        </section>
      </div>
    </div>
  );
}
